import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, Eye, EyeOff } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FirebaseContext, api } from 'common';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import CountrySelect from '../CountrySelect';
import AlertDialog from '../AlertDialog';
import CircularProgress from '@mui/material/CircularProgress';
import { WEB_MAIN_COLOR } from '../../common/sharedFunctions';
import PageSEO from '../SEO/PageSEO';

const ModernLogin = () => {
  const { authRef, RecaptchaVerifier, signInWithPhoneNumber } = useContext(FirebaseContext);
  const { t, i18n } = useTranslation();
  const {
    googleLogin, clearLoginError, mobileSignIn, countries,
    sendResetMail, verifyEmailPassword, requestMobileOtp, verifyMobileOtp,
    mainSignUp, checkUserExists, validateReferer
  } = api;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const countryList = countries;

  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [capatchaReady, setCapatchaReady] = useState(false);
  const [verifier, setVerifier] = useState(null);
  const [mobileWithoutCountry, setMobileWithoutCountry] = useState('');
  const [message, setMessage] = useState(false);
  const [otpCalled, setOtpCalled] = useState(false);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const [data, setData] = useState({
    email: '', confirmpassword: '', country: '', contact: '',
    mobile: '', password: '', otp: '', verificationId: null,
    firstName: '', lastName: '', selectedcountry: null,
    usertype: 'customer', referralId: '', entryType: null
  });

  useEffect(() => {
    if (settings && countryList) {
      for (let i = 0; i < countryList.length; i++) {
        if (countryList[i].label === settings.country) {
          setData(prev => ({ ...prev, country: countryList[i].phone, selectedcountry: countryList[i] }));
        }
      }
    }
  }, [settings]);

  useEffect(() => {
    if (!window.recaptchaVerifier) setCapatchaReady(true);
    if (auth.profile?.uid) {
      const role = auth.profile.usertype;
      if (role === 'admin' || role === 'fleetadmin') navigate('/dashboard');
      else if (role === 'driver') navigate('/bookings');
      else navigate('/');
    }
    if (auth.error?.flag && auth.error.msg.message !== t('not_logged_in')) {
      if (auth.error.msg.message === t('require_approval')) {
        setCommonAlert({ open: true, msg: t('require_approval') });
      } else if (auth.error.msg.message === 'Firebase: Error (auth/invalid-verification-code).') {
        setCommonAlert({ open: true, msg: t('login_error') });
        setIsLoading(false);
      } else if (data.contact && data.contact !== '') {
        setCommonAlert({ open: true, msg: t('login_error') });
        setIsLoading(false);
      }
    }
    if (auth.verificationId && otpCalled) {
      setOtpCalled(false);
      setData(prev => ({ ...prev, verificationId: auth.verificationId }));
    }
  }, [auth.profile, auth.error, auth.verificationId, navigate, data.contact, otpCalled, t]);

  const handleGoogleLogin = (credentialResponse) => {
    if (credentialResponse?.credential) {
      dispatch(googleLogin(credentialResponse.credential, null));
    } else {
      setCommonAlert({ open: true, msg: t('login_error') });
    }
  };

  const onInputChange = (e) => setData(prev => ({ ...prev, [e.target.id]: e.target.value }));

  const onCountryChange = (_, value) => {
    if (value?.phone) {
      setData(prev => ({ ...prev, country: value.phone, selectedcountry: value }));
      setMobileWithoutCountry('');
    }
  };

  const mobileInputChange = (e) => {
    const val = e.target.value;
    setMobileWithoutCountry(val);
    setData(prev => ({ ...prev, mobile: '+' + prev.country + val.replace(/ /g, '') }));
  };

  const handleGetOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!data.country) { setCommonAlert({ open: true, msg: t('country_blank_error') }); setIsLoading(false); return; }
    if (!data.contact) { setCommonAlert({ open: true, msg: t('contact_input_error') }); setIsLoading(false); return; }
    if (isNaN(data.contact)) {
      setData(prev => ({ ...prev, entryType: 'email' }));
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (re.test(data.contact)) {
        await dispatch(verifyEmailPassword(data.contact, data.otp));
      } else {
        setCommonAlert({ open: true, msg: t('proper_email') });
        setIsLoading(false);
      }
    } else {
      setData(prev => ({ ...prev, entryType: 'mobile' }));
      if (!settings.AllowCriticalEditsAdmin) { setCommonAlert({ open: true, msg: t('in_demo_mobile_login') }); }
      const formattedNum = data.contact.replace(/ /g, '');
      const phoneNumber = '+' + data.country + formattedNum;
      if (formattedNum.length > 6) {
        if (settings.customMobileOTP) {
          dispatch(requestMobileOtp(phoneNumber));
          setData(prev => ({ ...prev, verificationId: true }));
          setIsLoading(false);
        } else {
          if (!window.recaptchaVerifier || verifier === null) {
            window.recaptchaVerifier = await new RecaptchaVerifier(authRef(), 'sign-in-button', {
              size: 'invisible',
              callback: () => setCapatchaReady(true)
            });
          }
          const appVerifier = window.recaptchaVerifier;
          setVerifier(appVerifier);
          await signInWithPhoneNumber(authRef(), phoneNumber, appVerifier)
            .then(res => { setData(prev => ({ ...prev, verificationId: res.verificationId })); setIsLoading(false); window.recaptchaVerifier.clear(); })
            .catch(error => { setCommonAlert({ open: true, msg: error.code + ': ' + error.message }); setIsLoading(false); });
        }
      } else {
        setCommonAlert({ open: true, msg: t('mobile_no_blank_error') });
        setIsLoading(false);
      }
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (data.otp?.length === 6) {
      if (settings.customMobileOTP) {
        dispatch(verifyMobileOtp('+' + data.country + data.contact.replace(/ /g, ''), data.otp));
      } else {
        await dispatch(mobileSignIn(data.verificationId, data.otp));
      }
      setIsLoading(false);
    } else {
      setCommonAlert({ open: true, msg: t('otp_validate_error') });
      setIsLoading(false);
    }
  };

  const handleCancel = () => setData(prev => ({ ...prev, contact: null, verificationId: null }));

  const forgotPassword = async () => {
    setShowForgotModal(true);
    setResetEmail(data.contact || '');
  };

  const handleSendResetEmail = async () => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(resetEmail)) {
      setCommonAlert({ open: true, msg: t('proper_email') });
      return;
    }
    setResetLoading(true);
    try {
      await dispatch(sendResetMail(resetEmail));
      setResetSuccess(true);
      setTimeout(() => {
        setShowForgotModal(false);
        setResetSuccess(false);
        setResetEmail('');
      }, 3000);
    } catch (error) {
      setCommonAlert({ open: true, msg: t('error') || 'Error sending email' });
    }
    setResetLoading(false);
  };

  const checkPasswordValidity = (value) => {
    if (value !== data.confirmpassword) return t('confirm_password_not_match_err');
    if (!/^\S*$/.test(value)) return t('password_must_not_contain_whitespaces');
    if (!/^(?=.*[A-Z]).*$/.test(value)) return t('password_must_have_at_least_one_uppercase_character');
    if (!/^(?=.*[a-z]).*$/.test(value)) return t('password_must_have_at_least_one_lowercase_character');
    if (!/^(?=.*[0-9]).*$/.test(value)) return t('password_must_contain_at_least_one_digit');
    if (!/^.{8,16}$/.test(value)) return t('password_must_be_8-16_characters_long');
    return null;
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    new Promise((resolve, reject) => {
      setIsLoading(true);
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const validatePassword = checkPasswordValidity(data.password);
      if (!re.test(data.email)) { setCommonAlert({ open: true, msg: t('proper_email') }); return reject(); }
      if (!(/\S/.test(data.firstName) && data.firstName.length > 0 && /\S/.test(data.lastName) && data.lastName.length > 0)) { setCommonAlert({ open: true, msg: t('proper_input_name') }); return reject(); }
      if (validatePassword) { setCommonAlert({ open: true, msg: validatePassword }); return reject(); }
      if (mobileWithoutCountry.length < 6) { setCommonAlert({ open: true, msg: t('mobile_no_blank_error') }); return reject(); }
      const userData = { ...data };
      if (userData.usertype === 'customer') delete userData.carType;
      delete userData.confirmpassword;
      delete userData.pass;
      checkUserExists(userData).then((res) => {
        if (res.users?.length > 0) { setCommonAlert({ open: true, msg: t('user_exists') }); setIsLoading(false); return; }
        const doSignUp = (ud) => mainSignUp(ud).then((res) => {
          if (res.uid) {
            setData(prev => ({ ...prev, email: '', firstName: '', lastName: '', password: '', confirmpassword: '', usertype: 'customer', referralId: '' }));
            setMobileWithoutCountry('');
            setMessage(true);
            setIsLoading(false);
            resolve();
          } else { setCommonAlert({ open: true, msg: t('reg_error') }); setIsLoading(false); }
        });
        delete userData.country;
        if (userData.referralId?.length > 0) {
          validateReferer(userData.referralId).then((info) => {
            if (info.uid) doSignUp({ ...userData, signupViaReferral: info.uid });
            else { setCommonAlert({ open: true, msg: t('referer_not_found') }); setIsLoading(false); }
          }).catch(() => { setCommonAlert({ open: true, msg: t('referer_not_found') }); setIsLoading(false); });
        } else { doSignUp(userData); }
      });
    }).catch(() => {}).finally(() => setIsLoading(false));
  };

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' });
    if (auth.error?.flag) { setData(prev => ({ ...prev, email: '', pass: '' })); dispatch(clearLoginError()); }
  };

  const inputClass = "w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#009689] focus:border-transparent text-sm bg-white";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#009689]/5 via-[#319F44]/5 to-[#009689]/5 p-4">
      <PageSEO
        title="Login - Access Your Account"
        description="Sign in to Gruwcer Delivery to book rides, track deliveries, manage your bookings, and access your wallet. Register as a customer or driver."
        keywords="login, sign in, register, create account, driver login, customer login, ride booking account"
        path="/login"
      />
      <div id="sign-in-button" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <img
            src="/gruwcer-logo.png"
            alt="Gruwcer Delivery"
            onClick={() => window.location.href = '/'}
            className="h-12 w-auto object-contain mx-auto cursor-pointer hover:opacity-80 transition-opacity"
          />
          <p className="text-gray-500 mt-1 text-sm">Your delivery, your way</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-bold text-center text-gray-800 mb-6">
            {isSignUp ? t('signup') : t('login')}
          </h2>

          {/* Google Login */}
          {!isSignUp && settings?.socialLogin && (
            <div className="flex justify-center mb-4">
              <GoogleLogin onSuccess={handleGoogleLogin} onError={handleGoogleLogin} />
            </div>
          )}

          {!isSignUp && settings?.socialLogin && (
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-gray-400">{t('or')}</span></div>
            </div>
          )}

          <form className="space-y-4">
            {/* Sign Up Fields */}
            {isSignUp && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>{t('first_name')}</label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input id="firstName" className={inputClass} placeholder="John" onChange={onInputChange} value={data.firstName} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>{t('last_name')}</label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input id="lastName" className={inputClass} placeholder="Doe" onChange={onInputChange} value={data.lastName} />
                    </div>
                  </div>
                </div>

                {data.selectedcountry && countryList?.length > 0 && (
                  <CountrySelect
                    countries={countryList}
                    label={t('select_country')}
                    value={data.selectedcountry}
                    onChange={onCountryChange}
                    readOnly={!settings?.AllowCountrySelection}
                  />
                )}

                <div>
                  <label className={labelClass}>{t('phone')}</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input className={inputClass} placeholder="Phone number" onChange={mobileInputChange} value={mobileWithoutCountry} />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>{t('User Type')}</label>
                  <select
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#009689] text-sm bg-white"
                    value={data.usertype}
                    onChange={(e) => setData(prev => ({ ...prev, usertype: e.target.value }))}
                  >
                    <option value="customer">{t('customer')}</option>
                    <option value="driver">{t('driver')}</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>{t('email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input id="email" type="email" className={inputClass} placeholder="name@example.com" onChange={onInputChange} value={data.email} />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>{t('password')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input id="password" type={showPassword ? 'text' : 'password'} className={inputClass + ' pr-10'} placeholder="••••••••" onChange={onInputChange} value={data.password} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-gray-400">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>{t('confirm_password')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input id="confirmpassword" type={showConfirmPassword ? 'text' : 'password'} className={inputClass + ' pr-10'} placeholder="••••••••" onChange={onInputChange} value={data.confirmpassword} />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-2.5 text-gray-400">
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>{t('referralId')}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input id="referralId" className={inputClass} placeholder="Referral ID (optional)" onChange={onInputChange} value={data.referralId} />
                  </div>
                </div>
              </>
            )}

            {/* Login Fields */}
            {!isSignUp && (
              <>
                {data.selectedcountry && settings?.mobileLogin && countryList?.length > 0 && (
                  <CountrySelect
                    countries={countryList}
                    label={t('select_country')}
                    value={data.selectedcountry}
                    onChange={onCountryChange}
                    disabled={!!data.verificationId}
                    readOnly={!settings?.AllowCountrySelection}
                  />
                )}

                <div>
                  <label className={labelClass}>
                    {settings?.emailLogin && settings?.mobileLogin ? t('contact_placeholder') : settings?.emailLogin ? t('email_id') : t('mobile_number')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      id="contact"
                      className={inputClass}
                      placeholder={settings?.emailLogin && settings?.mobileLogin ? 'Email or phone' : settings?.emailLogin ? 'Email' : 'Phone'}
                      onChange={onInputChange}
                      value={data.contact}
                      disabled={!!data.verificationId}
                    />
                  </div>
                </div>

                {((data.contact && isNaN(data.contact)) || (settings?.emailLogin && !settings?.mobileLogin)) && (
                  <div>
                    <label className={labelClass}>{t('password')}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input id="otp" type={showLoginPassword ? 'text' : 'password'} className={inputClass + ' pr-10'} placeholder="••••••••" onChange={onInputChange} value={data.otp} autoComplete="off" />
                      <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                        {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {data.verificationId && (
                  <div>
                    <label className={labelClass}>{t('otp')}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input id="otp" type="password" className={inputClass} placeholder="6-digit OTP" onChange={onInputChange} value={data.otp} autoComplete="off" />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Actions */}
            {isLoading ? (
              <div className="flex justify-center py-2"><CircularProgress size={28} /></div>
            ) : (
              <div className="space-y-2 pt-1">
                {isSignUp ? (
                  message ? (
                    <div className="text-center">
                      <p className="text-green-600 font-semibold text-sm mb-2">{t('account_create_successfully')}</p>
                      <button type="button" onClick={() => { setIsSignUp(false); setMessage(false); }} className="text-[#009689] hover:text-[#007a6e] text-sm font-medium">
                        {t('signIn')}
                      </button>
                    </div>
                  ) : (
                    <button type="submit" onClick={handleSignUp} className="w-full py-2.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-[#009689] to-[#319F44] hover:from-[#007a6e] hover:to-[#267a35] transition-all">
                      {t('signup')}
                    </button>
                  )
                ) : !data.verificationId ? (
                  <>
                    <button type="submit" onClick={handleGetOTP} className="w-full py-2.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-[#009689] to-[#319F44] hover:from-[#007a6e] hover:to-[#267a35] transition-all">
                      {settings?.mobileLogin ? (data.contact && isNaN(data.contact) ? t('login') : t('login_otp')) : t('login')}
                    </button>
                    <button type="button" onClick={forgotPassword} className="w-full text-center text-sm text-[#009689] hover:text-[#007a6e] font-medium py-1">
                      {t('forgot_password')}
                    </button>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <button type="submit" onClick={handleVerifyOTP} className="flex-1 py-2.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-[#009689] to-[#319F44] hover:from-[#007a6e] hover:to-[#267a35] transition-all">
                      {t('verify_otp')}
                    </button>
                    <button type="button" onClick={handleCancel} className="flex-1 py-2.5 rounded-2xl font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 transition-all">
                      {t('cancel')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>

          {!isLoading && (
            <p className="text-center text-sm text-gray-500 mt-4">
              {isSignUp ? t('already_account') + ' ' : t('no_account') + ' '}
              <button type="button" onClick={() => { setIsSignUp(!isSignUp); setMessage(false); }} className="text-[#009689] hover:text-[#007a6e] font-semibold">
                {isSignUp ? t('signIn') : t('signup')}
              </button>
            </p>
          )}

          {!settings?.AllowCriticalEditsAdmin && (
            <div className="mt-4 p-3 bg-gray-50 rounded-2xl text-center text-xs text-gray-500">
              <p>Email: support@gruwcer.com</p>
              <p>Password: Admin@123</p>
            </div>
          )}
        </div>
      </motion.div>

      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowForgotModal(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden p-6"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('forgot_password')}</h3>
            <p className="text-gray-600 text-sm mb-4">{t('enter_email_for_reset') || 'Enter your email address and we\'ll send you a link to reset your password.'}</p>
            
            {resetSuccess ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 text-green-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="font-medium">{t('email_send')}</span>
                </div>
                <p className="text-green-600 text-sm mt-1">{t('check_spam') || 'Please check your spam/junk folder if you don\'t see the email.'}</p>
              </div>
            ) : (
              <>
                <div className="relative mb-4">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#009689] focus:border-transparent text-sm bg-white"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowForgotModal(false)}
                    className="flex-1 py-2.5 rounded-2xl font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 transition-all"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleSendResetEmail}
                    disabled={resetLoading}
                    className="flex-1 py-2.5 rounded-2xl font-semibold text-white bg-gradient-to-r from-[#009689] to-[#319F44] hover:from-[#007a6e] hover:to-[#267a35] transition-all flex items-center justify-center gap-2"
                  >
                    {resetLoading ? (
                      <CircularProgress size={20} style={{ color: 'white' }} />
                    ) : (
                      t('send') || 'Send Reset Link'
                    )}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ModernLogin;
